/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { autorun } from 'mobx';

import { useGuard } from '../../guard';
import { processItems } from '../helpers';
import { ContextMenu } from '../components';
import { ContextMenuStore, ContextMenuStoreContext } from '../store';

import type { OffsetPosition, UseMenuProps } from '../interfaces';

interface UseContextMenuActions {
  show: (position: OffsetPosition) => void;
  hide: () => void;
}

export const useMenu = <RowDataT, TotalRowDataT, FilterT>(
    { gridApi }: UseMenuProps<RowDataT, TotalRowDataT, FilterT>
): [JSX.Element, UseContextMenuActions] => {
    const { canAction } = useGuard();

    const [offsetPosition, setOffsetPosition] = useState<OffsetPosition>({
        x: 0,
        y: 0,
    });

    const menuStore = useMemo(() => new ContextMenuStore(), []);

    useEffect(() => {
        const autorunDisposer = autorun(() => {
            const { selectionMode, selected, loading, contextMenuActions: items } = gridApi;

            if (!items.length || items.every(x => x.hidden)) {
                menuStore.setMenuItems([]);
                return;
            }

            const processedItems = processItems(items, selected, selectionMode, loading, canAction);
            menuStore.setMenuItems(processedItems);
        });

        return autorunDisposer;
    }, []);

    useEffect(() => {
        const listener = () => {
            menuStore.hideMenu();
        };

        window.addEventListener('resize', listener, true);

        return () => {
            window.removeEventListener('resize', listener, true);
        };
    }, []);

    return [
        <ContextMenuStoreContext.Provider value={menuStore}>
            <ContextMenu offsetPosition={offsetPosition} />
        </ContextMenuStoreContext.Provider>,
        {
            show: (offsetPosition: OffsetPosition) => {
                setOffsetPosition(offsetPosition);
                menuStore.showMenu();
            },
            hide: menuStore.hideMenu,
        },
    ];
};
